import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { requestRefresh, type RefreshTopic } from '../../lib/resourceRefresh';
import type {
  AssistantChange,
  AssistantChangeStatus,
  AssistantResourceType,
} from '../../types/api';

/**
 * What each kind of record the assistant edits is shown by: the one map both
 * the stream's `change` frames and Undo go through, so the open page re-reads
 * the same lists whichever way the record moved.
 *
 * - Knowledge-base records also move the readiness score.
 * - Key dates are the `calendar_events` rows the knowledge base and the
 *   calendar share.
 * - A campaign's name and colour are drawn on its planned posts.
 * - A run is its review screen and every history list (Home, History, the
 *   assistant's own Waiting chip).
 */
export const RESOURCE_REFRESH_TOPICS: Readonly<
  Record<AssistantResourceType, readonly RefreshTopic[]>
> = {
  brand_identity: ['brand_identity', 'kb_readiness'],
  product: ['products', 'kb_readiness'],
  voice_example: ['voice_examples', 'kb_readiness'],
  proof_point: ['proof_points', 'kb_readiness'],
  key_date: ['calendar_events', 'kb_readiness'],
  planned_post: ['planned_posts'],
  campaign: ['campaigns', 'planned_posts'],
  month_goals: ['content_goals'],
  run: ['runs'],
  memory: ['brand_memories'],
};

/** Tells every open screen showing this record to re-read it. */
export function refreshForResource(
  businessId: string,
  resource: { type: AssistantResourceType; id: string | null },
): void {
  // A type from a newer server than this build: nothing here shows it.
  const topics = RESOURCE_REFRESH_TOPICS[resource.type] ?? [];
  requestRefresh(businessId, topics, resource.id);
}

/** Tells every open screen showing this change's record to re-read it. */
export function refreshForChange(businessId: string, change: AssistantChange): void {
  refreshForResource(businessId, change.resource);
}

/** Whether this build knows a resource type (a newer server may send others). */
export function isKnownResourceType(value: unknown): value is AssistantResourceType {
  return typeof value === 'string' && value in RESOURCE_REFRESH_TOPICS;
}

const RESOURCE_TYPES = new Set<string>(Object.keys(RESOURCE_REFRESH_TOPICS));
const CHANGE_STATUSES = new Set<string>([
  'done',
  'undone',
  'failed',
] satisfies AssistantChangeStatus[]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** One change, narrowed rather than cast: a stream frame is untrusted. */
function parseChange(value: unknown): AssistantChange | null {
  if (!isRecord(value)) return null;
  const { action_id, tool, summary, resource, status, undoable, created_at } = value;
  if (typeof action_id !== 'string' || !action_id) return null;
  if (typeof summary !== 'string' || !summary) return null;
  if (typeof status !== 'string' || !CHANGE_STATUSES.has(status)) return null;
  if (!isRecord(resource) || typeof resource.type !== 'string') return null;
  if (!RESOURCE_TYPES.has(resource.type)) return null;
  return {
    action_id,
    tool: typeof tool === 'string' ? tool : '',
    summary,
    resource: {
      type: resource.type as AssistantResourceType,
      id: typeof resource.id === 'string' ? resource.id : null,
    },
    status: status as AssistantChangeStatus,
    undoable: undoable === true,
    created_at: typeof created_at === 'string' ? created_at : '',
  };
}

/** One SSE `change` frame: `{reply_to, change}`. */
export function parseChangeEvent(
  data: unknown,
): { replyTo: string | null; change: AssistantChange } | null {
  if (typeof data !== 'string') return null;
  try {
    const parsed: unknown = JSON.parse(data);
    if (!isRecord(parsed)) return null;
    const change = parseChange(parsed.change);
    if (change === null) return null;
    return {
      replyTo: typeof parsed.reply_to === 'string' ? parsed.reply_to : null,
      change,
    };
  } catch {
    return null;
  }
}

/** Codes where the server's own sentence is the answer, and the row has moved on. */
const SETTLED_UNDO_CODES = new Set([
  'ASSISTANT_UNDO_STALE',
  'ASSISTANT_UNDO_DONE',
  'ASSISTANT_UNDO_EXPIRED',
  'ASSISTANT_NOT_UNDOABLE',
]);

/** True when a failed undo means the thread's copy of the change is out of date. */
export function isSettledUndoError(caught: unknown): boolean {
  return (
    caught instanceof ApiError &&
    (caught.status === 404 || (caught.code !== null && SETTLED_UNDO_CODES.has(caught.code)))
  );
}

/** A failed undo in words: the server's message for the known refusals. */
export function undoFailureMessage(caught: unknown): string {
  if (caught instanceof ApiError && caught.status === 404) {
    return 'That change is no longer there to undo.';
  }
  return getErrorMessage(caught, 'That did not undo. Try again.');
}
